import React, { useCallback, useMemo, useRef } from "react";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  AutocompleteRenderGetTagProps,
  createFilterOptions
} from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import useReportFilterLock from "features/reports/hooks/use-report-filter-lock.hook";
import { useGetClinicsQuery } from "services/clinics";
import { Clinic } from "services/types/clinics.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CLINIC_FILTER_QUERY =
  "{id,name,logo,display_name,surcharge_percent,effective_surcharge_percent,extra{billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_zip,billng_phone},street_address_1,street_address_2,city,state,zip_code,phone,region{id},practice{id,logo,name}}";

export type ClinicOption = {
  label: string;
  value: string;
  region: number | undefined;
};

const SELECT_ALL_VALUE = "__select_all__";
const SELECT_ALL_OPTION: ClinicOption = {
  label: "Select All",
  value: SELECT_ALL_VALUE,
  region: undefined
};

const baseFilterOptions = createFilterOptions<ClinicOption>({
  matchFrom: "any",
  stringify: (option) => option.label
});

const MAX_VISIBLE_CHIPS = 3;
const OVERFLOW_TOOLTIP_MAX_NAMES = 20;

type SelectClinicProps = {
  label?: string;
  onOptionSelected?: (
    option: ClinicOption | ClinicOption[] | null,
    clinics: Clinic[]
  ) => void;
  required?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  enablePortal?: boolean;
  filterByRegions?: number[];
  skipFilteringByUser?: boolean;
  showAllRegions?: boolean;
  enableSelectAll?: boolean;
  sx?: SxProps;
};

export type RhfSelectClinic<T extends FieldValues> = SelectClinicProps &
  UseControllerProps<T>;

export default function SelectClinic<T extends FieldValues>({
  control,
  label = "By Clinic",
  name,
  onOptionSelected,
  required = false,
  multiple = false,
  openOnFocus = false,
  enablePortal = false,
  autoFocus,
  filterByRegions,
  skipFilteringByUser,
  showAllRegions = false,
  enableSelectAll = false,
  sx
}: RhfSelectClinic<T>) {
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentUser = useAppSelector(selectCurrentUser);

  const isShowOwnClinicsOnlyEnabled = currentUser.show_own_clinics_only;

  // ENG-1595: on a report scoped to specific clinics, only those are offered here.
  const filterLock = useReportFilterLock();
  const isClinicScoped = filterLock.scopedClinicFilter === name;
  const allowedClinicIds = isClinicScoped
    ? filterLock.allowedClinicIds
    : undefined;

  const { data: clinics = [], isLoading } = useGetClinicsQuery(
    {
      query: CLINIC_FILTER_QUERY,
      region: showAllRegions
        ? undefined
        : (filterByRegions ?? currentRegion?.id),
      user: isShowOwnClinicsOnlyEnabled
        ? currentUser?.id
        : showAllRegions
          ? undefined
          : !skipFilteringByUser && currentRegion
            ? currentUser?.id
            : undefined
    },
    {
      skip:
        !currentUser?.id &&
        !currentClinic?.id &&
        hasRegions &&
        !currentRegion?.id
    }
  );

  const clinicOptions = useMemo(() => {
    const mapped = clinics.map((clinic) => ({
      label: clinic.name,
      value: String(clinic.id),
      region: clinic.region?.id
    }));
    return allowedClinicIds
      ? mapped.filter((option) => allowedClinicIds.includes(option.value))
      : mapped;
  }, [clinics, allowedClinicIds]);

  // With a single scoped clinic there is nothing to choose, so the filter is locked to it.
  const isClinicLocked = Boolean(
    allowedClinicIds && allowedClinicIds.length === 1
  );

  const options = useMemo(
    () =>
      enableSelectAll && multiple
        ? [SELECT_ALL_OPTION, ...clinicOptions]
        : clinicOptions,
    [enableSelectAll, multiple, clinicOptions]
  );

  const filterOptions = useMemo(
    () =>
      enableSelectAll && multiple
        ? (
            opts: ClinicOption[],
            state: Parameters<typeof baseFilterOptions>[1]
          ) => {
            const filtered = baseFilterOptions(
              opts.filter((o) => o.value !== SELECT_ALL_VALUE),
              state
            );
            return [SELECT_ALL_OPTION, ...filtered];
          }
        : baseFilterOptions,
    [enableSelectAll, multiple]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  // A scoped user picks freely among their clinics, but clearing the selection outright would send
  // no clinic at all and quietly return every clinic the scope excludes. Empty means "all of mine",
  // however the selection got emptied.
  const applyScopeFallback = useCallback(
    (selected: ClinicOption | ClinicOption[] | null) =>
      isClinicScoped && Array.isArray(selected) && !selected.length
        ? clinicOptions
        : selected,
    [isClinicScoped, clinicOptions]
  );

  const transformValue = useCallback(
    (selected: ClinicOption | ClinicOption[] | null) => {
      if (
        multiple &&
        enableSelectAll &&
        Array.isArray(selected) &&
        selected.some((o) => o.value === SELECT_ALL_VALUE)
      ) {
        const currentValues = selected.filter(
          (o) => o.value !== SELECT_ALL_VALUE
        );
        const allAlreadySelected = clinicOptions.every((o) =>
          currentValues.some((v) => v.value === o.value)
        );
        inputRef.current?.blur();
        return applyScopeFallback(
          (allAlreadySelected ? [] : clinicOptions) as ClinicOption[]
        );
      }
      return applyScopeFallback(
        Array.isArray(selected)
          ? selected.filter((o) => o.value !== SELECT_ALL_VALUE)
          : selected
      );
    },
    [multiple, enableSelectAll, clinicOptions, applyScopeFallback]
  );

  // Second controller on the same field as AutocompleteFormField's — only used
  // to clear the whole selection from the "All Clinics" summary chip, which
  // renders outside the Autocomplete's own change flow. Registered without
  // rules so the wrapper's `required` validation is untouched.
  const {
    field: { onChange: onFieldChange }
  } = useController<T>({ name, control });

  // Collapsed chips (opt-in via enableSelectAll): every clinic selected
  // renders as a single "All Clinics (N)" summary chip whose ✕ clears the
  // selection; partial selections cap at MAX_VISIBLE_CHIPS plus a "+N"
  // overflow chip so the filter never grows past one row.
  const renderCollapsedTags = useCallback(
    (value: ClinicOption[], getTagProps: AutocompleteRenderGetTagProps) => {
      const allSelected =
        clinicOptions.length > 1 &&
        clinicOptions.every((o) => value.some((v) => v.value === o.value));

      if (allSelected) {
        return (
          <Chip
            label={`All Clinics (${value.length})`}
            // This clear writes to RHF directly, outside the Autocomplete's onChange, so it must
            // apply the scope fallback itself — otherwise a scoped user's clear sends no clinic at
            // all and quietly returns every clinic the scope excludes.
            onDelete={() => onFieldChange(applyScopeFallback([]))}
          />
        );
      }

      const visible = value.slice(0, MAX_VISIBLE_CHIPS);
      const hidden = value.slice(MAX_VISIBLE_CHIPS);
      const tooltipNames = hidden
        .slice(0, OVERFLOW_TOOLTIP_MAX_NAMES)
        .map((o) => o.label)
        .join(", ");
      const tooltipSuffix =
        hidden.length > OVERFLOW_TOOLTIP_MAX_NAMES
          ? ` and ${hidden.length - OVERFLOW_TOOLTIP_MAX_NAMES} more`
          : "";

      return (
        <>
          {visible.map((option, index) => {
            const { key: _key, ...tagProps } = getTagProps({ index }) as {
              key: React.Key;
            } & React.HTMLAttributes<HTMLDivElement>;
            return (
              <Chip key={option.value} label={option.label} {...tagProps} />
            );
          })}
          {hidden.length > 0 && (
            <Tooltip title={`${tooltipNames}${tooltipSuffix}`}>
              <Chip label={`+${hidden.length}`} />
            </Tooltip>
          )}
        </>
      );
    },
    [clinicOptions, onFieldChange, applyScopeFallback]
  );

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      multiple={multiple}
      label={label}
      isLoading={isLoading}
      required={required}
      filterOptions={filterOptions}
      options={options}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => {
        const isSelectAll = option?.value === SELECT_ALL_VALUE;
        return (
          <ListItem {...props} key={option?.value}>
            {multiple && !isSelectAll && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            <ListItemText
              primaryTypographyProps={
                isSelectAll ? { fontWeight: "bold" } : undefined
              }
            >
              {option?.label}
            </ListItemText>
          </ListItem>
        );
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      disabled={isClinicLocked}
      control={control}
      transformValue={transformValue as any}
      renderTags={
        enableSelectAll && multiple ? (renderCollapsedTags as any) : undefined
      }
      inputRef={inputRef}
      onOptionSelected={(option) => {
        if (onOptionSelected) {
          requestAnimationFrame(() => {
            onOptionSelected(option, clinics);
          });
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
